<script lang="ts" setup>
import { SancareOcticon } from '@sancare/typescript-commons'
import { computed, Ref, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { McoStaySuggestionItem, McoStaySuggestionPrediction, McoStaySuggestionWildcardChild, SuggestionJustification } from '@/misc/types'
import MedicalActForm from '@/stay-displayer/medical-act/MedicalActForm.vue'
import { sortSuggestedMedicalActs } from '@/stay-displayer/medical-act/sortSuggestedMedicalActs'
import { MedicalActSuggestion } from '@/stay-displayer/medical-act/types'
import { CodingSelection } from '@/stay-displayer/types'
import { CodedActChop, CodedMedicalAct } from '@/store/modules/medical-act/types'
import { CountryEnum, PmsiTypeEnum } from '@/store/modules/settings/types'
import { Rum } from '@/store/modules/stay/mco/types'
import { StayHealthData } from '@/store/modules/stay/types'

import MedicalAct from './MedicalAct.vue'
import SuggestedMedicalAct from './SuggestedMedicalAct.vue'

const { t } = useI18n()

const emit = defineEmits(['make-selection'])
const props = withDefaults(defineProps<{
  codedMedicalActs: (CodedMedicalAct | CodedActChop)[]
  healthData: StayHealthData
  pmsiCountry: CountryEnum
  rum: Rum<CodedMedicalAct> | Rum<CodedActChop>
  suggestionJustifications?: Record<string, SuggestionJustification> | null
  // Optional: fetched separately from the main stay payload and merged in once it resolves.
  staySuggestions?: McoStaySuggestionItem[]
}>(), {
  suggestionJustifications: null,
  staySuggestions: () => [],
})

const actFormVisible = ref(false)
const preSelectedMedicalAct = ref<{ reference: string, description: string } | null>(null)

const isReadOnly = computed(() => props.rum.id === 0)
const visibleActs = computed(() => props.codedMedicalActs.filter((act) => !act.disabled))
const visibleActRefs = computed(() => visibleActs.value.map((medicalAct) => medicalAct.associatedAct.reference))

// The backend aggregates wildcard-matched searches on the parent item, not on each wildcard child
// (a real act matched only via the wildcard pattern has its own `searches` empty — see
// SuggestionRepository::getSuggestionsMcoForStay's searches_agg, grouped by the wildcard parent
// reference) — every child is suggested by the parent's searches plus any of its own.
function realSuggestionItems(item: McoStaySuggestionItem): (McoStaySuggestionItem | McoStaySuggestionWildcardChild)[] {
  if (!item.isWildcard) {
    return [item]
  }
  return item.wildcardChildren.map((child) => {
    const searchesById = new Map([...item.searches, ...child.searches].map((search) => [search.sancareId, search]))
    return { ...child, searches: Array.from(searchesById.values()) }
  })
}

// The backend aggregates predictions across every RUM of the stay on the suggestion item itself, so
// the currently displayed RUM's own prediction is looked up by its rum scope (same pattern as
// DiagnosisListStandardMode.vue's resolvePredictions).
function resolvePredictedAct(predictions: McoStaySuggestionPrediction[]): McoStaySuggestionPrediction | null {
  return predictions.find((prediction) => prediction.rum.sancareId === props.rum.id) ?? null
}

const visibleSuggestedActs = computed<MedicalActSuggestion[]>(() => {
  const result: MedicalActSuggestion[] = []

  for (const item of props.staySuggestions) {
    if (item.type !== 'medicalActs') {
      continue
    }

    for (const real of realSuggestionItems(item)) {
      if (visibleActRefs.value.includes(real.reference)) {
        continue
      }

      const prediction = resolvePredictedAct(real.predictions)

      if (real.searches.length) {
        result.push({
          reference: real.reference,
          description: real.description ?? '',
          searches: real.searches.map((search) => ({ search: { id: search.sancareId, name: search.name } })),
          prediction,
        })
      } else if (prediction) {
        result.push({ reference: real.reference, description: real.description ?? '', searches: [], prediction })
      }
    }
  }

  return sortSuggestedMedicalActs(result)
})

function promoteMedicalAct(suggestion: MedicalActSuggestion) {
  preSelectedMedicalAct.value = {
    reference: suggestion.reference,
    description: suggestion.description,
  }
  actFormVisible.value = true
}
function makeSelection(selection: CodingSelection) {
  emit('make-selection', selection)
}
</script>

<template>
  <section>
    <div class="row g-0 align-items-star card-container col-12">
      <div class="card acts-card col-7 mb-0">
        <div class="card-body">
          <h1 class="card-title d-flex align-items-center mb-3">
            <span>{{ t('medicalAct.title', 2).toUpperCase() }}</span>
            <small class="ms-1 fs-6">- Codage en cours</small>
          </h1>
          <ul
            v-if="visibleActs.length"
            class="list-unstyled medical-act p-0 m-0"
          >
            <li
              v-for="codedMedicalAct in visibleActs"
              :key="codedMedicalAct.id"
              class="px-0 mt-3"
            >
              <medical-act
                :pmsi-country="pmsiCountry"
                :coded-medical-act="codedMedicalAct"
                :readonly="isReadOnly"
                :rum-id="rum.id"
              />
            </li>
          </ul>
          <div
            v-else
            class="text-center"
          >
            {{ $t('medicalAct.codedNone') }}
          </div>
        </div>
      </div>
      <div class="card dark acts-card col-5 m-0">
        <div class="py-1 px-3">
          <h1 class="card-title d-flex align-items-center mb-2">
            <span>{{ $t('medicalAct.title', 2).toUpperCase() }}</span>
            <small class="ms-1 fs-6">- {{ $t('stayDisplayer.sancareSuggestion', visibleSuggestedActs.length) }}</small>
          </h1>
        </div>
        <ul
          v-if="visibleSuggestedActs.length"
          class="list-unstyled predicted-medical-act p-0 m-0"
        >
          <li
            v-for="suggestion in visibleSuggestedActs"
            :key="suggestion.reference"
            class="p-2 d-flex align-items-start"
          >
            <suggested-medical-act
              :health-data="healthData"
              :suggestion="suggestion"
              :suggestion-justifications="suggestionJustifications"
              :readonly="isReadOnly"
              @make-selection="makeSelection"
              @promote-medical-act="promoteMedicalAct"
            />
          </li>
        </ul>
        <div
          v-else
          class="text-center"
        >
          {{ $t('medicalAct.suggestedNone') }}
        </div>
      </div>
    </div>
    <div
      v-if="!isReadOnly"
      style="margin-top: 5px;"
    >
      <div
        class="row g-0 col-5 btn btn-coding-element btn-medical-act px-2"
        @click="actFormVisible = !actFormVisible"
      >
        <div
          class="col-auto icon-block px-2"
        >
          <sancare-octicon
            name="plus"
            :width="15"
            :height="15"
          />
        </div>
        <span class="col-auto">
          {{ $t('medicalAct.manual') }}
        </span>
      </div>
      <medical-act-form
        :summary-unit="rum"
        :pmsi-type="PmsiTypeEnum.MCO"
        :pmsi-country="pmsiCountry"
        :pre-selected-medical-act="preSelectedMedicalAct"
        :visible="actFormVisible"
        @close-form="actFormVisible = false"
      />
    </div>
  </section>
</template>

<style lang="scss" scoped>
@use 'sass:color';

@import '@sancare/typescript-commons/src/assets/styles/variables';
@import '@/assets/styles/button';

$medical-act-grey: #687e93;

.card {
  &.dark {
    background-color: $medical-act-grey;
    color: #ffffff;

    & li {
      border-top: 1px solid transparent;
      border-bottom: 1px solid transparent;

      &:hover {
        border-top: 1px solid color.change(#ffffff, $alpha: .5);
        border-bottom: 1px solid color.change(#ffffff, $alpha: .5);
        background-color: color.change(#ffffff, $alpha: .1);
      }
    }
  }
}

.btn-medical-act {
  background-color: color.adjust($medical-act-grey, $lightness: 45%);
  @include btn-coding-element($medical-act-grey);
}

.card-title {
  font-size: 2rem;
}
</style>
